import toJsDate from '../dataHelpers/toJsDate'
import ladderSteps from '../participationHelpers/ladderSteps'

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T12)
// AI-CONTEXT: See docs/pitfalls.md#security-public-page-reads-its-own-gates
// De deelnemerwidgets op de openbare link lezen geen Firestore (ADR-022): alles
// komt mee met getPublicLiveReport2 als `personaData` (publicPersonaData.js).
// Deze functies zetten dat antwoord om in precies wat de widgets in de app van
// hun eigen laad-hook krijgen, zodat de weergave gelijk blijft.
//
// Faalt dicht: ontbreekt een soort in het antwoord (een oudere server, een
// widget die de server niet kende), dan weten we het niet. Dat is een fout,
// nooit "niets geregistreerd" — dat laatste is een bewering aan de lezer.

/** @typedef {import('../../StateManagment/PublicReportContext').PublicPersonaData} PublicPersonaData */

const notInResponse = () => new Error('Niet meegestuurd in het antwoord van de openbare rapportage')

/** @param {object} goal @returns {number} */
const positionOf = (goal) => (typeof goal.Position === 'number' ? goal.Position : Infinity)

/**
 * Doelen op Position; zonder Position achteraan. Eén volgorde voor de app
 * (usePersonaGoals) en de link.
 * @param {Array<object>} goals
 * @returns {Array<object>} Een nieuwe array
 */
export const sortGoalsByPosition = (goals) => [...goals].sort((a, b) => positionOf(a) - positionOf(b))

/**
 * Zelfde grenzen als de Firestore-query van usePersonaPresenceRange: inclusief,
 * `null` = open, en met een grens valt een regel zonder datum weg.
 *
 * @param {PublicPersonaData|null|undefined} personaData
 * @param {number|null} startMillis
 * @param {number|null} endMillis
 * @returns {import('../../hooks/usePersonaPresenceRange').PresenceRangeResult}
 */
export const publicPresenceState = (personaData, startMillis, endMillis) => {
    if (!Array.isArray(personaData?.presence)) return { reports: [], loaded: false, error: notInResponse() }
    const reports = personaData.presence.filter((report) => {
        if (startMillis == null && endMillis == null) return true
        const date = toJsDate(report.Timestamp)
        if (!date) return false
        const ms = date.getTime()
        return (startMillis == null || ms >= startMillis) && (endMillis == null || ms <= endMillis)
    })
    return { reports, loaded: true, error: null }
}

/**
 * @param {PublicPersonaData|null|undefined} personaData
 * @returns {{ goals: Array<object>|null, effectNameById: Object<string, string>|null, error: Error|null }}
 */
export const publicGoalsState = (personaData) => {
    if (!Array.isArray(personaData?.goals)) return { goals: null, effectNameById: null, error: notInResponse() }
    const effects = Array.isArray(personaData.effects) ? personaData.effects : []
    return {
        goals: sortGoalsByPosition(personaData.goals),
        effectNameById: Object.fromEntries(effects.map((effect) => [effect.ID, effect.Effect])),
        error: null,
    }
}

/**
 * Zelfde toestanden als usePersonaProfile.
 * @param {PublicPersonaData|null|undefined} personaData
 * @returns {{ status: import('../../components/liveReport/reportBuilder2/widgets/_shared/usePersonaProfile').PersonaProfileStatus, profile: Object<string, *> }}
 */
export const publicProfileState = (personaData) => {
    // `persona: null` = de server vond de deelnemer niet (meer) bij de eigenaar (scenario 21).
    if (personaData && personaData.persona === null) return { status: 'missing', profile: {} }
    const profile = personaData?.profile
    if (!profile || typeof profile !== 'object') return { status: 'error', profile: {} }
    return { status: 'ready', profile }
}

/**
 * Zelfde toestanden als usePersonaLadder. De server stuurt alleen de twee
 * treden; het verschil rekenen we hier, met dezelfde regel als in de app.
 * @param {PublicPersonaData|null|undefined} personaData
 * @returns {{ status: 'error'|'missing'|'ready', steps: import('../participationHelpers/ladderSteps').LadderSteps|null }}
 */
export const publicLadderState = (personaData) => {
    if (personaData && personaData.persona === null) return { status: 'missing', steps: null }
    const ladder = personaData?.ladder
    if (!ladder || typeof ladder !== 'object') return { status: 'error', steps: null }
    return { status: 'ready', steps: ladderSteps(ladder.begin, ladder.current) }
}

/**
 * Zelfde toestanden als usePersonaWorkSkills.
 * @param {PublicPersonaData|null|undefined} personaData
 * @returns {{ status: 'error'|'missing'|'ready', assessments: Array<object> }}
 */
export const publicWorkSkillsState = (personaData) => {
    if (personaData && personaData.persona === null) return { status: 'missing', assessments: [] }
    if (!Array.isArray(personaData?.workSkills)) return { status: 'error', assessments: [] }
    return { status: 'ready', assessments: personaData.workSkills }
}

const publicPersonaState = { publicPresenceState, publicGoalsState, publicProfileState, publicLadderState, publicWorkSkillsState, sortGoalsByPosition }

export default publicPersonaState
